import { useState, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { Box, Button, Typography } from '@mui/material';
import { Modal } from '../../../components';
import {
  SignaturePad,
  SignaturePadHandle
} from '../../../components/signature-canvas/SignaturePad';
import { verifyWalkthroughCompletion } from '../../../fetch';
import { base64StringToBlob } from '../../../helpers/download';
import { ModalSaveSection } from '../../../components/modal/ModalSaveSection';

interface IVerifyCompleteModal {
  open: boolean;
  onClose: (shouldRefetch?: boolean) => void;
  fieldWalkthroughId: string;
  onSaving: (isSaving: boolean) => void;
}

export const VerifyCompleteModal = ({
  open,
  onClose,
  fieldWalkthroughId,
  onSaving
}: IVerifyCompleteModal) => {
  const { enqueueSnackbar } = useSnackbar();
  const signaturePadRef = useRef<SignaturePadHandle>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSignaturePresent, setIsSignaturePresent] = useState(false);

  const handlePadEnd = () => {
    if (signaturePadRef.current && typeof signaturePadRef.current.isEmpty === 'function') {
      setIsSignaturePresent(!signaturePadRef.current.isEmpty());
    }
  };

  const handleClear = () => {
    signaturePadRef.current?.clear();
    setIsSignaturePresent(false);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      onSaving(true);
      const signatureDataUrl = signaturePadRef.current?.getTrimmedDataURL('image/png');
      if (!signatureDataUrl) {
        enqueueSnackbar('Please provide a signature', { variant: 'error' });
        return;
      }

      const base64Data = signatureDataUrl.split(',')[1];
      const blob = base64StringToBlob(base64Data, 'image/png');
      const formData = new FormData();
      formData.append('Signature', blob, 'signature.png');

      await verifyWalkthroughCompletion(fieldWalkthroughId, formData);
      enqueueSnackbar('Verified successfully. Verification report generated', {
        variant: 'success'
      });
      handleClear();
      onClose(true);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error completing walkthrough', {
        variant: 'error'
      });
    } finally {
      setIsSaving(false);
      onSaving(false);
    }
  };

  const handleClose = () => {
    if (signaturePadRef.current?.getTrimmedDataURL() && isSignaturePresent) {
      const result = window.confirm('You have unsaved changes, are you sure you want to exit?');
      if (result) {
        handleClear();
        onClose();
      }
    } else {
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={handleClose} maxWidth="sm">
      <Box>
        <Typography variant="h5" gutterBottom>
          Verify Added Items
        </Typography>

        <Box>
          <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
            Signature
          </Typography>
          <SignaturePad ref={signaturePadRef} onEnd={handlePadEnd} />
          {isSignaturePresent && (
            <Box display="flex" justifyContent="flex-end">
              <Button variant="text" color="primary" onClick={handleClear}>
                Clear Signature
              </Button>
            </Box>
          )}
        </Box>

        <ModalSaveSection
          marginTop="0.5rem"
          handleCancel={handleClose}
          isSaveDisabled={isSaving || !isSignaturePresent}
          handleSave={handleSave}
        />
      </Box>
    </Modal>
  );
};
